import React from "react";
import { useProductInfo } from "../../hooks/useProductInfo";
import { useListingTypeConfig } from "../../hooks/useListingTypeConfig";
import { useCtaState } from "../../hooks/useCtaState";
import { useSellerContact } from "../../hooks/useSellerContact";
import { useCanManageListing } from "../../hooks/useCanManageListing";
import genericUtility from "../../../../../../services/utility/GenericUtility";
import SliderCta from "./SliderCta";
import ButtonCta from "./ButtonCta";
import GuestCta from "./GuestCta";
import OfferModal from "../../../ProductDetails/childComponents/OfferModal";
import AutoShareContact from "./AutoShareContact";
import "./styles/CtaSection.css";

const CtaSection: React.FC = () => {
  const { productId, isCurrentUserSeller, businessId, listingType, price, contactMode, phone, title, sellerId, isSold } = useProductInfo();
  const canManageListing = useCanManageListing(isCurrentUserSeller, businessId);
  const phoneNumber = useSellerContact(sellerId, contactMode, phone);
  const isAuthenticated = genericUtility.isUserLoggedIn();
  const { ctaStrategy } = useListingTypeConfig(listingType);
  const ctaState = useCtaState(productId, isAuthenticated);
  const { isOfferOpen, handleOfferSuccess, handleOfferCancel, navigateToLoginForOffer } = ctaState;

  // Show "You listed this" to the seller, or to the business owner /
  // collaborator on their own business's listing — in either case they
  // shouldn't see a CTA to buy from themselves.
  if (canManageListing) {
    return (
      <div style={{ padding: "0 1rem 2rem", textAlign: "center", color: "#6b7280", fontSize: "0.875rem" }}>
        You listed this
      </div>
    );
  }

  // A sold listing stays fully viewable to everyone (see ProductCard's greyed
  // SOLD state) but must not take new inquiries — so this sits ahead of BOTH
  // the guest and signed-in CTA paths. Owners keep "You listed this" above.
  if (isSold) {
    return (
      <div className="cta-sold-wrap">
        <span className="cta-sold-pill">This item has been sold</span>
      </div>
    );
  }

  if (ctaStrategy.style === "none") return null;

  if (!isAuthenticated) {
    return <GuestCta cta={ctaStrategy} onLoginClick={navigateToLoginForOffer} />;
  }

  return (
    <>
      {ctaStrategy.style === "slider" && (
        <SliderCta cta={ctaStrategy} ctaState={ctaState} />
      )}
      {ctaStrategy.style === "button" && (
        <ButtonCta cta={ctaStrategy} ctaState={ctaState} />
      )}

      {contactMode === "AUTO" ? (
        <AutoShareContact
          isOpen={isOfferOpen}
          onClose={handleOfferCancel}
          phone={phoneNumber}
          productTitle={title}
        />
      ) : (
        <OfferModal
          isOpen={isOfferOpen}
          onClose={handleOfferCancel}
          onOfferSuccess={handleOfferSuccess}
          onOfferCancel={handleOfferCancel}
          initialPrice={price > 0 ? price : undefined}
        />
      )}
    </>
  );
};

export default CtaSection;
